import type { CommandResult, PendingOperation, UUID } from '../../domain/types';
import { emit, isLocal, subscribe, type BusMessage } from '../../lib/bus';
import type { PresenceInfo, SyncRepository } from '../types';
import type { UpdatePersonCommand } from '../types';
import { freshSession } from './actor';
import { MockDb } from './MockDb';
import { MockPersonRepository } from './MockPersonRepository';

const PRESENCE_TTL = 12_000;

export class MockSyncRepository implements SyncRepository {
  private heartbeats = new Map<UUID, ReturnType<typeof setInterval>>();
  private presence = new Map<UUID, Map<UUID, PresenceInfo>>(); // entityId → accountId → info
  private presenceListeners = new Map<UUID, Set<(others: PresenceInfo[]) => void>>();
  private onlineListeners = new Set<(online: boolean) => void>();

  constructor(private db: MockDb, private people: MockPersonRepository) {
    subscribe((m) => this.onBus(m));
    db.onSim(() => this.notifyOnline());
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.notifyOnline());
      window.addEventListener('offline', () => this.notifyOnline());
      window.addEventListener('beforeunload', () => {
        for (const id of this.heartbeats.keys()) this.announceEditing(id, false);
      });
    }
  }

  private onBus(m: BusMessage): void {
    if (m.type !== 'presence' || isLocal(m)) return;
    const map = this.presence.get(m.entityId) ?? new Map<UUID, PresenceInfo>();
    if (m.editing) map.set(m.accountId, { entityId: m.entityId, accountId: m.accountId, displayName: m.displayName, at: m.at });
    else map.delete(m.accountId);
    this.presence.set(m.entityId, map);
    this.fire(m.entityId);
  }

  private fire(entityId: UUID): void {
    const listeners = this.presenceListeners.get(entityId);
    if (!listeners?.size) return;
    const me = this.db.getSession()?.accountId;
    const now = Date.now();
    const map = this.presence.get(entityId) ?? new Map();
    for (const [acc, info] of map) if (now - info.at > PRESENCE_TTL) map.delete(acc);
    const others = [...map.values()].filter((p) => p.accountId !== me);
    for (const l of listeners) l(others);
  }

  onChange(listener: (tables: string[]) => void): () => void {
    return subscribe((m) => {
      if (!isLocal(m)) return;
      if (m.type === 'changed') listener(m.tables);
      if (m.type === 'reset') listener(['*']);
    });
  }

  announceEditing(entityId: UUID, editing: boolean): void {
    const s = this.db.getSession();
    if (!s) return;
    const send = () => emit({ type: 'presence', entityId, accountId: s.accountId, displayName: this.db.accountName(s.accountId), editing, at: Date.now() });
    send();
    const existing = this.heartbeats.get(entityId);
    if (existing) clearInterval(existing);
    this.heartbeats.delete(entityId);
    if (editing) this.heartbeats.set(entityId, setInterval(send, 4000));
  }

  onPresence(entityId: UUID, listener: (others: PresenceInfo[]) => void): () => void {
    const set = this.presenceListeners.get(entityId) ?? new Set();
    set.add(listener);
    this.presenceListeners.set(entityId, set);
    const sweep = setInterval(() => this.fire(entityId), 4000);
    this.fire(entityId);
    return () => {
      set.delete(listener);
      clearInterval(sweep);
    };
  }

  isOnline(): boolean {
    return !this.db.isOffline();
  }

  isStoragePersistent(): boolean {
    return this.db.isPersistent();
  }

  private notifyOnline(): void {
    const online = this.isOnline();
    for (const l of this.onlineListeners) l(online);
  }

  onOnlineChange(listener: (online: boolean) => void): () => void {
    this.onlineListeners.add(listener);
    return () => this.onlineListeners.delete(listener);
  }

  async listPendingOperations(): Promise<PendingOperation[]> {
    await this.db.ready;
    const s = this.db.getSession();
    return this.db.t.pending_operations.filter((op) => !s || op.accountId === s.accountId).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async replayPending(): Promise<Array<{ op: PendingOperation; result: CommandResult<unknown> }>> {
    await this.db.ready;
    const out: Array<{ op: PendingOperation; result: CommandResult<unknown> }> = [];
    if (this.db.isOffline()) return out;
    const s = freshSession(this.db);
    if (!s) return out;
    const ops = this.db.t.pending_operations.filter((op) => op.accountId === s.accountId).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const op of ops) {
      let result: CommandResult<unknown>;
      switch (op.command) {
        case 'people.update':
          result = await this.people.replay(op.payload as unknown as UpdatePersonCommand);
          break;
        default:
          result = { ok: false, kind: 'validation', messageKey: 'error.unexpected' };
      }
      op.attempts += 1;
      if (result.ok || (result.kind !== 'network')) {
        // Applied, merged, or turned into a conflict/review — either way it leaves the queue (a conflict is surfaced to the user).
        this.db.t.pending_operations = this.db.t.pending_operations.filter((x) => x.id !== op.id);
      } else {
        op.lastError = result.messageKey;
      }
      out.push({ op, result });
    }
    await this.db.commit(['pending_operations']);
    return out;
  }

  async discardPending(id: UUID): Promise<void> {
    await this.db.ready;
    this.db.t.pending_operations = this.db.t.pending_operations.filter((x) => x.id !== id);
    await this.db.commit(['pending_operations']);
  }
}
